import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import ManualLeadForm from "@/components/ManualLeadForm";
import PriorInteractions from "@/components/PriorInteractions";
import SpinnerButton from "@/components/SpinnerButton";
import WonDealFields from "@/components/WonDealFields";
import { adminApi, apiBase } from "@/lib/api";

const TYPES = ["All", "Email", "WhatsApp", "Appointment", "Walk-in"];
const STATUSES = ["All", "New", "Contacted", "In Progress", "Closed Won", "Closed Lost"];
const ALLOWED_STATUSES = ["New", "Contacted", "In Progress", "Closed Won", "Closed Lost"];
const PAGE_SIZE = 50;

// Mirrors LOST_REASONS in backend/server.py, which validates the value
// on save. Hardcoded here the same way STATUSES already is — keep the
// two lists in step.
const LOST_REASONS = ["Price", "Bought elsewhere", "Stock unavailable", "No response from customer", "Not a serious buyer", "Other"];

// Quick-range buttons. Each returns [from, to] in YYYY-MM-DD MYT.
function ymd(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }
function quickRange(kind) {
  const now = new Date();
  if (kind === "today") return [ymd(now), ymd(now)];
  if (kind === "week") {
    // Mon-Sun, treating Monday as start of week
    const dow = (now.getDay() + 6) % 7; // 0 = Monday
    const start = new Date(now); start.setDate(now.getDate() - dow);
    const end = new Date(start); end.setDate(start.getDate() + 6);
    return [ymd(start), ymd(end)];
  }
  if (kind === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return [ymd(start), ymd(end)];
  }
  if (kind === "30d") {
    const start = new Date(now); start.setDate(now.getDate() - 29);
    return [ymd(start), ymd(now)];
  }
  if (kind === "90d") {
    const start = new Date(now); start.setDate(now.getDate() - 89);
    return [ymd(start), ymd(now)];
  }
  return ["", ""];
}

function Drawer({ lead, onClose, onUpdate }) {
  const [status, setStatus] = useState(lead.Status);
  const [note, setNote] = useState("");
  const [apptDate, setApptDate] = useState(lead.Appointment_Date || "");
  const [apptTime, setApptTime] = useState(lead.Appointment_Time || "");
  const [wonValue, setWonValue] = useState(lead.Won_Value || "");
  const [wonSku, setWonSku] = useState(lead.Won_SKU || "");
  const [lostReason, setLostReason] = useState(lead.Lost_Reason || "");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr(""); setLoading(true);
    try {
      const body = { status, note };
      if (lead.Type === "Appointment") {
        body.appointment_date = apptDate;
        body.appointment_time = apptTime;
      }
      if (status === "Closed Won") {
        body.won_value = wonValue;
        body.won_sku = wonSku;
      }
      if (status === "Closed Lost") { body.lost_reason = lostReason; }
      const res = await adminApi.leadUpdate(lead.ID, body);
      onUpdate(res.data.lead);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally { setLoading(false); }
  };

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", display: "flex", justifyContent: "flex-end", zIndex: 50 }} onClick={onClose} data-testid="lead-drawer">
      <div style={{ width: "min(560px, 100%)", background: "#FFFFFF", height: "100%", overflowY: "auto", padding: 28 }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
          <p className="scw-eyebrow">Lead detail</p>
          <button onClick={onClose} className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }} data-testid="lead-drawer-close">Close</button>
        </div>
        <h2 style={{ fontSize: 24, margin: "0 0 18px 0" }}>{lead.Customer_Name}</h2>
        <PriorInteractions leadId={lead.ID} apiClient={adminApi} testIdPrefix="admin" />
        {/* Pass 55 — the review request no longer lives in this drawer.
            It has its own tab (/admin/reviews) where the whole pending
            queue is actionable in one place. The drawer was never the
            right home for it anyway: a lead only becomes review-eligible
            3 days after the win, by which point nobody is sitting in
            this drawer. */}
        <div style={{ borderTop: "1px solid var(--scw-line)", marginBottom: 24 }}>
          {[
            ["Type", lead.Type], ["Phone", lead.Customer_Phone], ["Email", lead.Customer_Email || "\u2014"],
            ["Product", lead.Product_Watch || "\u2014"], ["Message", lead.Message || "\u2014"],
            ["Appointment", lead.Appointment_Date ? `${lead.Appointment_Date} ${lead.Appointment_Time}` : "\u2014"],
            ["Duty", lead.Duty_Person || "\u2014"],
            ["Received", lead.Timestamp + " MYT"], ["Last update", lead.Last_Updated + " MYT"],
            ["Source", lead.Source || "\u2014"],
            ["UTM", [lead.UTM_Source, lead.UTM_Medium, lead.UTM_Campaign].filter(Boolean).join(" / ") || "\u2014"],
            ["Click IDs", [lead.Click_ID_Google && `gclid:${lead.Click_ID_Google}`, lead.Click_ID_Meta && `fbclid:${lead.Click_ID_Meta}`].filter(Boolean).join(" / ") || "\u2014"],
            ["Won value (MYR)", lead.Won_Value || "\u2014"],
            ["Won SKU", lead.Won_SKU || "\u2014"],
            ["Won date", lead.Won_Date ? `${lead.Won_Date} MYT` : "\u2014"],
          ].map(([k, v]) => (
            <div key={k} style={{ display: "flex", padding: "10px 0", borderBottom: "1px solid var(--scw-line)" }}>
              <div style={{ width: 130, color: "var(--scw-muted)", fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase" }}>{k}</div>
              <div style={{ flex: 1, whiteSpace: "pre-wrap" }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 18 }}>
          <label className="scw-label">Status</label>
          <select
            className="scw-select"
            value={status}
            onChange={(e) => {
              const next = e.target.value;
              setStatus(next);
              // Synchronous SKU prefill on the Closed-Won flip so the
              // operator sees the proposed SKU immediately (and can
              // edit it before saving). Skips when they've already
              // typed something.
              if (next === "Closed Won" && !wonSku && lead.Product_Watch) {
                setWonSku(lead.Product_Watch);
              }
            }}
            data-testid="lead-drawer-status"
          >
            {ALLOWED_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <WonDealFields
          visible={status === "Closed Won"}
          wonValue={wonValue}
          wonSku={wonSku}
          onChangeValue={setWonValue}
          onChangeSku={setWonSku}
        />
        {/* Pass 56 — required on the flip to Closed Lost. The server
            validates it too; this is the affordance, not the gate. */}
        {status === "Closed Lost" && (
          <div style={{ marginBottom: 18 }}>
            <label className="scw-label">Reason lost</label>
            <select
              className="scw-select"
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              data-testid="lead-drawer-lost-reason"
            >
              <option value="">Select a reason…</option>
              {LOST_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
        )}
        <div style={{ marginBottom: 18 }}>
          <label className="scw-label">Append note</label>
          <textarea className="scw-textarea" value={note} onChange={(e) => setNote(e.target.value)} data-testid="lead-drawer-note" />
        </div>
        {lead.Type === "Appointment" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 18 }}>
            <div>
              <label className="scw-label">Appointment date</label>
              <input className="scw-input" type="date" value={apptDate} onChange={(e) => setApptDate(e.target.value)} data-testid="lead-drawer-apptdate" />
            </div>
            <div>
              <label className="scw-label">Appointment time</label>
              <input className="scw-input" type="time" value={apptTime} onChange={(e) => setApptTime(e.target.value)} data-testid="lead-drawer-appttime" />
            </div>
          </div>
        )}
        {err && <p style={{ color: "#a13b3b", fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <SpinnerButton loading={loading} type="button" onClick={save} dataTestId="lead-drawer-save">Save</SpinnerButton>

        <div style={{ marginTop: 32 }}>
          <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Notes</p>
          <pre style={{ background: "#fafafa", padding: 14, fontFamily: "inherit", fontSize: 13, whiteSpace: "pre-wrap" }}>{lead.Notes || "\u2014"}</pre>
        </div>
        <div style={{ marginTop: 16 }}>
          <p className="scw-eyebrow" style={{ marginBottom: 8 }}>Status history</p>
          <pre style={{ background: "#fafafa", padding: 14, fontFamily: "inherit", fontSize: 13, whiteSpace: "pre-wrap" }}>{lead.Status_History || "\u2014"}</pre>
        </div>
      </div>
    </div>
  );
}

export default function AdminLeads() {
  const [filters, setFilters] = useState({ type: "All", status: "All", duty_person: "All", date_from: "", date_to: "", q: "" });
  const [page, setPage] = useState(1);
  const [leads, setLeads] = useState([]);
  const [count, setCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(false);
  const [team, setTeam] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [todayDuty, setTodayDuty] = useState("");
  const [toast, setToast] = useState("");
  // Pass 46 — `?open=<lead_id>` deep link from the dashboard pending
  // list auto-opens that lead's drawer once on mount. Consumed exactly
  // once so closing the drawer doesn't re-open it.
  const [searchParams, setSearchParams] = useSearchParams();
  const openLeadId = searchParams.get("open") || "";

  const today = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  }, []);

  useEffect(() => {
    // Populate Duty Person filter from active team members.
    adminApi.team().then((r) => {
      const active = (r.data.members || []).filter((m) => {
        const v = String(m.Active ?? "TRUE").trim().toUpperCase();
        return v === "" || v === "TRUE" || v === "1" || v === "YES";
      });
      setTeam(active);
    }).catch(() => setTeam([]));
    // Today's duty seeds the Add-Lead form's default attribution.
    adminApi.duty({ from: today, to: today })
      .then((r) => {
        const a = (r.data.assignments || [])[0];
        if (a && a.Person_Name) setTodayDuty(a.Person_Name);
      })
      .catch(() => { /* ignore — admin can pick from dropdown */ });
  }, [today]);

  const params = useMemo(() => {
    const p = { page, page_size: PAGE_SIZE };
    if (filters.type !== "All") p.type = filters.type;
    if (filters.status !== "All") p.status = filters.status;
    if (filters.duty_person !== "All") p.duty_person = filters.duty_person;
    if (filters.date_from) p.date_from = filters.date_from;
    if (filters.date_to) p.date_to = filters.date_to;
    if (filters.q) p.q = filters.q;
    return p;
  }, [filters, page]);

  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.leads(params);
      setLeads(res.data.leads);
      setCount(res.data.count);
      setTotalPages(res.data.total_pages || 1);
    } finally { setLoading(false); }
  }, [params]);

  useEffect(() => { load(); }, [load]);

  // Pass 46 — Auto-open via `?open=<id>`. Fetch directly (the row may
  // be outside the current filter/page slice), then clear the URL
  // param so the drawer behaves normally on close.
  useEffect(() => {
    if (!openLeadId) return;
    let cancelled = false;
    adminApi.leadDetail(openLeadId)
      .then((r) => { if (!cancelled && r.data?.lead) setActive(r.data.lead); })
      .catch(() => { /* stale / deleted link — ignore */ })
      .finally(() => { if (!cancelled) setSearchParams({}, { replace: true }); });
    return () => { cancelled = true; };
  }, [openLeadId, setSearchParams]);

  const exportCsv = () => {
    // CSV export uses the full filtered set (no pagination).
    const csvParams = { ...params };
    delete csvParams.page; delete csvParams.page_size;
    csvParams.format = "csv";
    const qs = new URLSearchParams(csvParams).toString();
    const url = `${apiBase}/admin/leads?${qs}`;
    window.open(url, "_blank");
  };

  const applyQuickRange = (kind) => {
    const [from, to] = quickRange(kind);
    setFilters((f) => ({ ...f, date_from: from, date_to: to }));
  };

  return (
    <AdminLayout>
      <p className="scw-eyebrow" style={{ marginBottom: 8 }}>All enquiries</p>
      <h1 style={{ fontSize: 28, margin: "0 0 28px 0" }}>Leads</h1>

      {/* Quick-range buttons */}
      <div className="scw-chip-row" style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
        <p className="scw-eyebrow" style={{ margin: "auto 8px auto 0" }}>Quick range:</p>
        {[
          ["today", "Today"],
          ["week", "This Week"],
          ["month", "This Month"],
          ["30d", "Last 30 Days"],
          ["90d", "Last 90 Days"],
        ].map(([k, label]) => (
          <button
            key={k}
            type="button"
            className="scw-btn scw-btn-ghost"
            style={{ padding: "6px 14px", fontSize: 12 }}
            onClick={() => applyQuickRange(k)}
            data-testid={`leads-range-${k}`}
          >
            {label}
          </button>
        ))}
        {(filters.date_from || filters.date_to) && (
          <button
            type="button"
            className="scw-btn scw-btn-ghost"
            style={{ padding: "6px 14px", fontSize: 12, color: "var(--scw-muted)" }}
            onClick={() => setFilters((f) => ({ ...f, date_from: "", date_to: "" }))}
            data-testid="leads-range-clear"
          >
            Clear dates
          </button>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12, marginBottom: 24 }}>
        <div>
          <label className="scw-label">Type</label>
          <select className="scw-select" value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="leads-filter-type">
            {TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="scw-label">Status</label>
          <select className="scw-select" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="leads-filter-status">
            {STATUSES.map((s) => <option key={s}>{s}</option>)}
          </select>
        </div>
        <div>
          <label className="scw-label">Duty person</label>
          <select className="scw-select" value={filters.duty_person} onChange={(e) => setFilters({ ...filters, duty_person: e.target.value })} data-testid="leads-filter-duty">
            <option value="All">All</option>
            {team.map((m) => <option key={m.ID} value={m.Name}>{m.Name}</option>)}
          </select>
        </div>
        <div>
          <label className="scw-label">From</label>
          <input type="date" className="scw-input" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="leads-filter-from" />
        </div>
        <div>
          <label className="scw-label">To</label>
          <input type="date" className="scw-input" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="leads-filter-to" />
        </div>
        <div>
          <label className="scw-label">Search</label>
          <input className="scw-input" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} placeholder="Name, phone, or email" data-testid="leads-filter-q" />
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <span className="scw-eyebrow" data-testid="leads-count">{count} result{count === 1 ? "" : "s"}</span>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button onClick={() => setShowAdd(true)} className="scw-btn" style={{ padding: "10px 18px" }} data-testid="leads-add-btn">+ Add lead</button>
          <button onClick={exportCsv} className="scw-btn scw-btn-ghost" style={{ padding: "10px 18px" }} data-testid="leads-export-btn">Export CSV</button>
        </div>
      </div>
      {toast && (
        <p style={{ color: "#3a7d3a", fontSize: 13, marginBottom: 12 }} data-testid="leads-toast">{toast}</p>
      )}

      <div className="scw-card" style={{ padding: 0 }}>
        {loading ? (
          <p style={{ padding: 24 }}>{"Loading\u2026"}</p>
        ) : leads.length === 0 ? (
          <p style={{ padding: 24, color: "var(--scw-muted)" }} data-testid="leads-empty">No leads match the current filters.</p>
        ) : (
          <div className="scw-table-wrap">
            <table className="scw-table scw-leads-table">
              <thead><tr>
                <th>Timestamp</th><th>Type</th><th>Customer</th><th>Phone</th><th>Product</th><th>Duty</th><th>Status</th><th>Flags</th>
              </tr></thead>
              <tbody>
                {leads.map((r) => (
                  <tr key={r.ID} onClick={() => setActive(r)} style={{ cursor: "pointer" }} data-testid={`leads-row-${r.ID}`}>
                    <td data-label="When">{r.Timestamp}</td>
                    <td data-label="Type">{r.Type}</td>
                    <td data-label="Customer">{r.Customer_Name}</td>
                    <td data-label="Phone">{r.Customer_Phone}</td>
                    <td data-label="Product">{r.Product_Watch || "\u2014"}</td>
                    <td data-label="Duty">{r.Duty_Person || "\u2014"}</td>
                    <td data-label="Status"><span className="scw-pill">{r.Status}</span></td>
                    <td data-label="Flags">
                      {r.review_pending && (
                        <span
                          className="scw-pill"
                          data-testid={`leads-review-chip-${r.ID}`}
                          style={{ background: "#fff0d4", color: "#7a5a13", borderColor: "#f0d9a1" }}
                        >Review</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {count > 0 && totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16 }} data-testid="leads-pagination">
          <button
            type="button"
            className="scw-btn scw-btn-ghost"
            style={{ padding: "8px 16px" }}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            data-testid="leads-prev-btn"
          >
            {'\u2190'} Previous
          </button>
          <span className="scw-eyebrow" data-testid="leads-page-label">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="scw-btn scw-btn-ghost"
            style={{ padding: "8px 16px" }}
            disabled={page >= totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            data-testid="leads-next-btn"
          >
            Next {'\u2192'}
          </button>
        </div>
      )}

      {showAdd && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 60, padding: 20 }}
          onClick={() => setShowAdd(false)}
          data-testid="leads-add-modal"
        >
          <div
            style={{ width: "min(640px, 100%)", maxHeight: "90vh", overflowY: "auto", background: "#FFFFFF", padding: 28, borderRadius: 2 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div>
                <p className="scw-eyebrow" style={{ marginBottom: 6 }}>Manual entry</p>
                <h2 style={{ fontSize: 22, margin: 0 }}>Add lead</h2>
              </div>
              <button onClick={() => setShowAdd(false)} className="scw-btn scw-btn-ghost" style={{ padding: "8px 16px" }} data-testid="leads-add-close">Close</button>
            </div>
            <p style={{ fontSize: 13, color: "var(--scw-muted)", marginTop: 0, marginBottom: 20 }}>
              For enquiries received off-platform {"\u2014"} WhatsApp DM, Instagram, phone, walk-up, etc.
              Choose the team member who handled it (defaults to today{"\u2019"}s duty).
            </p>
            <ManualLeadForm
              teamNames={team.map((m) => m.Name)}
              defaultDuty={todayDuty}
              onCancel={() => setShowAdd(false)}
              submitLabel="Log lead"
              onSubmit={async (form) => {
                await adminApi.manualLead(form);
                setShowAdd(false);
                setToast("Lead logged.");
                setTimeout(() => setToast(""), 4000);
                // Always jump back to page 1 so the new row is visible at top.
                if (page === 1) load(); else setPage(1);
              }}
            />
          </div>
        </div>
      )}

      {active && (
        <Drawer
          lead={active}
          onClose={() => setActive(null)}
          onUpdate={(updated) => {
            setLeads((cur) => cur.map((r) => r.ID === updated.ID ? updated : r));
            setActive(updated);
          }}
        />
      )}
    </AdminLayout>
  );
}
